import React, { useState, useCallback } from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  Typography,
  LinearProgress,
  Alert,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import { useDropzone } from 'react-dropzone';
import { useRefresh, useNotify } from 'react-admin';
import auth from '../../auth/auth0';
import { getApiUrlForResource } from '../../services/apiUrlResolver';
import { PARTNER_MARKETING_ASSETS_RESOURCE_NAME } from '../partnerMarketingAssets.model';
import logger from '../../utils/logger';

const SUPPORTED_TYPES = {
  'application/pdf': 'pdf',
  'image/png': 'png',
  'image/jpeg': 'jpg',
  'image/gif': 'gif',
  'image/webp': 'webp',
};

const MAX_FILES = 10;
const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB
const MAX_TOTAL_SIZE = 200 * 1024 * 1024; // 200MB

interface FileUploadState {
  file: File;
  status: 'pending' | 'uploading' | 'success' | 'error';
  progress: number;
  error?: string;
  publicUrl?: string;
  presignedData?: {
    upload_url: string;
    s3_key: string;
    public_url: string;
  };
}

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

const AssetBulkUploadButton = () => {
  const [open, setOpen] = useState(false);
  const [folderPath, setFolderPath] = useState('');
  const [files, setFiles] = useState<FileUploadState[]>([]);
  const [overallStatus, setOverallStatus] = useState<'idle' | 'uploading' | 'success' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);

  const refresh = useRefresh();
  const notify = useNotify();

  const resetState = () => {
    setFiles([]);
    setFolderPath('');
    setOverallStatus('idle');
    setError(null);
  };

  /**
   * Clean up orphaned S3 files that were uploaded but failed to create database records.
   * This prevents storage leaks when uploads succeed but record creation fails.
   */
  const cleanupOrphanedFiles = async (s3Keys: string[]) => {
    if (s3Keys.length === 0) return;

    const token = auth.getIdToken();
    const apiUrl = getApiUrlForResource(PARTNER_MARKETING_ASSETS_RESOURCE_NAME);

    // Clean up each orphaned file
    const cleanupPromises = s3Keys.map(async (s3Key) => {
      try {
        await fetch(`${apiUrl}/${PARTNER_MARKETING_ASSETS_RESOURCE_NAME}/cleanup`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ s3_key: s3Key }),
        });
        logger.info('Cleaned up orphaned S3 file after failed record creation', { s3_key: s3Key });
      } catch (cleanupError) {
        logger.error('Failed to cleanup orphaned S3 file', {
          s3_key: s3Key,
          error: cleanupError,
        });
      }
    });

    await Promise.all(cleanupPromises);
  };

  const handleOpen = () => {
    resetState();
    setOpen(true);
  };

  const handleClose = () => {
    if (overallStatus === 'uploading') {
      return; // Don't close while uploading
    }
    setOpen(false);
    resetState();
    if (overallStatus === 'success') {
      refresh();
    }
  };

  const removeFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const validateFiles = (newFiles: File[]): string | null => {
    // Check count
    if (files.length + newFiles.length > MAX_FILES) {
      return `Maximum ${MAX_FILES} files allowed. You have ${files.length} files and tried to add ${newFiles.length}.`;
    }

    // Check individual file sizes and types
    for (const file of newFiles) {
      if (!Object.keys(SUPPORTED_TYPES).includes(file.type)) {
        return `Unsupported file type: ${file.type} for file ${file.name}. Supported: PDF, PNG, JPG, GIF, WebP`;
      }
      if (file.size > MAX_FILE_SIZE) {
        return `File ${file.name} exceeds maximum size of ${formatFileSize(MAX_FILE_SIZE)}`;
      }
    }

    // Check total size
    const currentTotal = files.reduce((sum, f) => sum + f.file.size, 0);
    const newTotal = newFiles.reduce((sum, f) => sum + f.size, 0);
    if (currentTotal + newTotal > MAX_TOTAL_SIZE) {
      return `Total size exceeds maximum of ${formatFileSize(MAX_TOTAL_SIZE)}`;
    }

    return null;
  };

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const validationError = validateFiles(acceptedFiles);
    if (validationError) {
      setError(validationError);
      return;
    }

    setError(null);
    const newFiles: FileUploadState[] = acceptedFiles.map(file => ({
      file,
      status: 'pending',
      progress: 0,
    }));
    setFiles(prev => [...prev, ...newFiles]);
  }, [files]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'image/png': ['.png'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/gif': ['.gif'],
      'image/webp': ['.webp'],
    },
    maxFiles: MAX_FILES,
    disabled: overallStatus === 'uploading' || overallStatus === 'success',
  });

  const handleBulkUpload = async () => {
    if (files.length === 0) return;

    const token = auth.getIdToken();
    const apiUrl = getApiUrlForResource(PARTNER_MARKETING_ASSETS_RESOURCE_NAME);

    setOverallStatus('uploading');
    setError(null);

    // Track successfully uploaded S3 keys for cleanup on failure
    let uploadedS3KeysForCleanup: string[] = [];

    try {
      // Step 1: Get bulk presigned URLs
      const presignedResponse = await fetch(
        `${apiUrl}/${PARTNER_MARKETING_ASSETS_RESOURCE_NAME}/bulk/presigned-url`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            files: files.map(f => ({
              filename: f.file.name,
              content_type: f.file.type,
              file_size_bytes: f.file.size,
            })),
            folder_path: folderPath,
          }),
        }
      );

      if (!presignedResponse.ok) {
        const errorData = await presignedResponse.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to get upload URLs');
      }

      const { presigned_urls } = await presignedResponse.json();

      // Update files with presigned data
      const updatedFiles = files.map((f, i) => ({
        ...f,
        presignedData: presigned_urls[i],
        status: 'uploading' as const,
        progress: 10,
      }));
      setFiles(updatedFiles);

      // Step 2: Upload all files to S3 in parallel
      const uploadPromises = updatedFiles.map(async (fileState, index) => {
        try {
          const uploadResponse = await fetch(fileState.presignedData!.upload_url, {
            method: 'PUT',
            headers: {
              'Content-Type': fileState.file.type,
            },
            body: fileState.file,
          });

          if (!uploadResponse.ok) {
            throw new Error(`Upload failed: ${uploadResponse.status}`);
          }

          // Track successfully uploaded file for cleanup if needed
          uploadedS3KeysForCleanup.push(fileState.presignedData!.s3_key);

          // Update progress
          setFiles(prev => {
            const updated = [...prev];
            updated[index] = { ...updated[index], progress: 70 };
            return updated;
          });

          return { index, success: true };
        } catch (error) {
          return { index, success: false, error: error instanceof Error ? error.message : 'Upload failed' };
        }
      });

      const uploadResults = await Promise.all(uploadPromises);

      // Step 3: Create asset records
      const successfulUploads = uploadResults.filter(r => r.success);
      const failedUploads = uploadResults.filter(r => !r.success);

      // Update failed files
      failedUploads.forEach(({ index, error: err }) => {
        setFiles(prev => {
          const updated = [...prev];
          updated[index] = {
            ...updated[index],
            status: 'error',
            error: err,
            progress: 0,
          };
          return updated;
        });
      });

      if (successfulUploads.length > 0) {
        // Track S3 keys of successfully uploaded files for cleanup if needed
        const uploadedS3Keys = successfulUploads.map(({ index }) => updatedFiles[index].presignedData!.s3_key);

        const createResponse = await fetch(
          `${apiUrl}/${PARTNER_MARKETING_ASSETS_RESOURCE_NAME}/bulk`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              assets: successfulUploads.map(({ index }) => {
                const fileState = updatedFiles[index];
                return {
                  s3_key: fileState.presignedData!.s3_key,
                  original_filename: fileState.file.name,
                  display_name: fileState.file.name,
                  folder_path: folderPath,
                  file_size_bytes: fileState.file.size,
                  content_type: fileState.file.type,
                };
              }),
            }),
          }
        );

        if (!createResponse.ok) {
          // Bulk create failed - clean up all successfully uploaded files
          await cleanupOrphanedFiles(uploadedS3Keys);
          // Remove cleaned files from cleanup tracking
          uploadedS3KeysForCleanup = uploadedS3KeysForCleanup.filter(
            key => !uploadedS3Keys.includes(key)
          );
          const errorData = await createResponse.json().catch(() => ({}));
          throw new Error(errorData.message || 'Failed to create asset records');
        }

        const createResult = await createResponse.json();

        // Track which files successfully created records
        const createdS3Keys = new Set(
          (createResult.created || []).map((asset: any) => asset.s3_key)
        );

        // Remove successfully created files from cleanup tracking
        uploadedS3KeysForCleanup = uploadedS3KeysForCleanup.filter(
          key => !createdS3Keys.has(key)
        );

        // Update successful files
        successfulUploads.forEach(({ index }, i) => {
          const createdAsset = createResult.created?.[i];
          const fileState = updatedFiles[index];
          if (createdS3Keys.has(fileState.presignedData!.s3_key)) {
            setFiles(prev => {
              const updated = [...prev];
              updated[index] = {
                ...updated[index],
                status: 'success',
                progress: 100,
                publicUrl: createdAsset?.public_url,
              };
              return updated;
            });
          }
        });

        // Handle partial failures from create response
        if (createResult.errors && createResult.errors.length > 0) {
          const orphanedS3Keys: string[] = [];
          createResult.errors.forEach((err: any) => {
            const fileIndex = updatedFiles.findIndex(f => f.file.name === err.filename);
            if (fileIndex !== -1) {
              const fileState = updatedFiles[fileIndex];
              // Track this file for cleanup
              if (fileState.presignedData?.s3_key) {
                orphanedS3Keys.push(fileState.presignedData.s3_key);
              }
              setFiles(prev => {
                const updated = [...prev];
                updated[fileIndex] = {
                  ...updated[fileIndex],
                  status: 'error',
                  error: err.error?.message || 'Failed to create record',
                };
                return updated;
              });
            }
          });
          // Clean up orphaned files from partial failures
          if (orphanedS3Keys.length > 0) {
            await cleanupOrphanedFiles(orphanedS3Keys);
            // Remove cleaned files from cleanup tracking
            uploadedS3KeysForCleanup = uploadedS3KeysForCleanup.filter(
              key => !orphanedS3Keys.includes(key)
            );
          }
        }

        // Check final success count
        setFiles(prev => {
          const finalSuccessCount = prev.filter(f => f.status === 'success').length;
          if (finalSuccessCount > 0) {
            setOverallStatus('success');
            notify(`Successfully uploaded ${finalSuccessCount} of ${prev.length} files`, { type: 'success' });
            refresh();
          } else {
            setOverallStatus('error');
            setError('All uploads failed');
          }
          return prev;
        });
      } else {
        // All uploads failed
        setOverallStatus('error');
        setError('All file uploads failed');
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Bulk upload failed';

      // Clean up any orphaned S3 files that were uploaded but failed to create records
      if (uploadedS3KeysForCleanup.length > 0) {
        await cleanupOrphanedFiles(uploadedS3KeysForCleanup);
      }

      setError(message);
      setOverallStatus('error');
      notify(message, { type: 'error' });
    }
  };

  const totalSize = files.reduce((sum, f) => sum + f.file.size, 0);
  const successCount = files.filter(f => f.status === 'success').length;
  const errorCount = files.filter(f => f.status === 'error').length;
  const pendingCount = files.filter(f => f.status === 'pending' || f.status === 'uploading').length;

  return (
    <>
      <Button
        variant="outlined"
        startIcon={<CloudUploadIcon />}
        onClick={handleOpen}
        size="small"
      >
        Bulk Upload
      </Button>

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          Bulk Upload Marketing Assets
          <IconButton
            onClick={handleClose}
            sx={{ position: 'absolute', right: 8, top: 8 }}
            disabled={overallStatus === 'uploading'}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          {overallStatus === 'success' ? (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <CheckCircleIcon color="success" sx={{ fontSize: 64, mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Upload Complete!
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Successfully uploaded {successCount} of {files.length} files
              </Typography>
              {errorCount > 0 && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  {errorCount} file(s) failed to upload
                </Alert>
              )}
            </Box>
          ) : (
            <>
              <Box
                {...getRootProps()}
                sx={{
                  border: '2px dashed',
                  borderColor: isDragActive ? 'primary.main' : 'grey.300',
                  borderRadius: 2,
                  p: 4,
                  textAlign: 'center',
                  cursor: overallStatus === 'uploading' ? 'not-allowed' : 'pointer',
                  bgcolor: isDragActive ? 'primary.light' : 'grey.50',
                  mb: 2,
                  opacity: overallStatus === 'uploading' ? 0.5 : 1,
                }}
              >
                <input {...getInputProps()} />
                <CloudUploadIcon sx={{ fontSize: 48, color: 'grey.500', mb: 1 }} />
                <Typography>
                  {files.length > 0
                    ? `${files.length} file(s) selected`
                    : isDragActive
                    ? 'Drop files here...'
                    : 'Drag & drop files here, or click to select'}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Max {MAX_FILES} files, {formatFileSize(MAX_FILE_SIZE)} per file, {formatFileSize(MAX_TOTAL_SIZE)} total
                  <br />
                  Supported: PDF, PNG, JPG, GIF, WebP
                </Typography>
              </Box>

              {files.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Selected Files ({files.length}/{MAX_FILES})
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                    Total size: {formatFileSize(totalSize)} / {formatFileSize(MAX_TOTAL_SIZE)}
                  </Typography>
                  <List dense>
                    {files.map((fileState, index) => (
                      <ListItem
                        key={index}
                        sx={{
                          border: '1px solid',
                          borderColor: 'grey.300',
                          borderRadius: 1,
                          mb: 1,
                          bgcolor: fileState.status === 'success' ? 'success.light' : fileState.status === 'error' ? 'error.light' : 'background.paper',
                        }}
                      >
                        <ListItemText
                          primary={fileState.file.name}
                          secondary={
                            <>
                              {formatFileSize(fileState.file.size)}
                              {fileState.status === 'uploading' && (
                                <Box sx={{ mt: 1 }}>
                                  <LinearProgress variant="determinate" value={fileState.progress} size="small" />
                                  <Typography variant="caption">{fileState.progress}%</Typography>
                                </Box>
                              )}
                              {fileState.status === 'error' && fileState.error && (
                                <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                                  {fileState.error}
                                </Typography>
                              )}
                              {fileState.status === 'success' && (
                                <Chip label="Success" color="success" size="small" sx={{ mt: 0.5 }} />
                              )}
                            </>
                          }
                        />
                        <ListItemSecondaryAction>
                          {fileState.status !== 'uploading' && (
                            <IconButton edge="end" onClick={() => removeFile(index)} size="small">
                              <DeleteIcon />
                            </IconButton>
                          )}
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              <TextField
                fullWidth
                label="Folder Path"
                value={folderPath}
                onChange={(e) => setFolderPath(e.target.value)}
                margin="normal"
                helperText="Optional folder for organization (e.g., 'partners/acme')"
                disabled={overallStatus === 'uploading'}
              />

              {error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {error}
                </Alert>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions>
          {overallStatus === 'success' ? (
            <Button onClick={handleClose} variant="contained">
              Done
            </Button>
          ) : (
            <>
              <Button onClick={handleClose} disabled={overallStatus === 'uploading'}>
                Cancel
              </Button>
              <Button
                onClick={handleBulkUpload}
                variant="contained"
                disabled={files.length === 0 || overallStatus === 'uploading'}
              >
                {overallStatus === 'uploading' ? `Uploading... (${pendingCount} remaining)` : `Upload ${files.length} File(s)`}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AssetBulkUploadButton;
